/* hs-search-enhance.js — header search intelligence (2026-08-20, +redirect 08-22).
 *
 * Four jobs, all progressive enhancements over the plain GET form in
 * snippets/hs-predictive-searchbar.liquid:
 *
 *  0. CATEGORY REDIRECT — an exact category-name query (incl. its vernacular
 *     and normalised forms) submits to the COLLECTION page, not /search. See
 *     COLLECTION_ROUTES below for the rules and the handle-verification note.
 *
 *  1. QUERY NORMALISATION — Hindi/Marathi vernacular and common misspellings
 *     map to catalogue terms BEFORE the predictive fetch and on full-form
 *     submit. Shopify's suggest endpoint was probed live on 2026-08-20: it has
 *     no typo tolerance and no theme-reachable synonym layer, so the mapping
 *     lives here. Every canonical target in the tables below was validated
 *     against /search/suggest.json (returned products) before inclusion — do
 *     not add an alias whose target you have not validated the same way.
 *
 *  2. TRENDING CHIPS — shows the server-rendered [data-hs-trending] panel when
 *     the input is focused while empty. The links exist in the HTML regardless
 *     (crawlable); this file only toggles visibility.
 *
 *  3. ROTATING PLACEHOLDER — cycles data-hs-placeholders (newline-separated).
 *     Pauses while focused, stops for good once the shopper types, and never
 *     runs under prefers-reduced-motion.
 *
 * It also replaces PredictiveSearch.updateSearchForTerm with a null-guarded
 * version. Once the fetched term can differ from the typed term (that is the
 * whole point of the alias map), the rendered "Search for X" text no longer
 * contains the previously typed term, and stock Dawn calls .match(...).length
 * on a null match — a TypeError on the next keystroke. The override also
 * regex-escapes the term, which stock Dawn does not.
 */
(function () {
  'use strict';

  /* Dictionary validated live against /search/suggest.json on 2026-08-20
   * (93 terms probed, relevance judged by returned titles, not counts —
   * the endpoint's fuzzy fallback fills slots even for off-topic words).
   *
   * Deliberate EXCLUSIONS, verified that day — do not "complete" them back in:
   *  - English misspellings (earing, pendent, braclet, sliver, solitare,
   *    neckless, ankelet, mangalsutr, jwellery…): Shopify's own fuzzy matching
   *    already corrects ALL of them to real products. Aliasing would add
   *    nothing and risk drift.
   *  - Vernacular NATIVE HITS (payal, nath, nathni, bali, bangles, locket,
   *    bichhiya/bichiya, jodvi, mangalsutra, nazariya, tops, ear tops, chandi,
   *    nose kil): these already return 4/4 relevant products as typed. They
   *    appear only in TOKEN_ALIASES (for multi-word queries where AND-matching
   *    would fail, e.g. "chandi payal"), never as whole-query rewrites.
   *  - kudi (meaning uncertain, hits looked incidental), oxidised/oxidized
   *    (catalogue has no finish-level titling to map to), choker as a TARGET
   *    (its own results are 0/4 relevant — thushi/galsari route to "necklace").
   */
  var ALIASES = {
    'jhumka': 'jhumki earrings',
    'jhumki': 'jhumki earrings',
    'jhumke': 'jhumki earrings',
    'chandbali': 'earrings',
    'bugadi': 'stud earrings',
    'latkan': 'pendant',
    'sui dhaga': 'bracelet',
    'laung': 'nose pin',
    'pajeb': 'anklet',
    'painjan': 'anklet',
    'ghungroo': 'anklet',
    'metti': 'toe ring',
    'vati': 'pendant',
    'tanmaniya': 'pendant',
    'kada': 'bracelet',
    'kangan': 'bracelet',
    'bangdi': 'bracelet',
    'chudi': 'bracelet',
    'haar': 'necklace',
    'mala': 'necklace',
    'thushi': 'necklace',
    'galsari': 'necklace',
    'anguthi': 'ring',
    'challa': 'ring',
    'chhalla': 'ring'
  };

  // Applied ONLY inside multi-word queries. A single vernacular word that
  // already hits natively ("payal") must stay untouched — remapping can only
  // narrow it — but inside "chandi payal" the untranslated token would AND-fail.
  var TOKEN_ALIASES = {
    'chandi': 'silver',
    'payal': 'anklet',
    'pajeb': 'anklet',
    'painjan': 'anklet',
    'ghungroo': 'anklet',
    'bali': 'hoop',
    'nath': 'nose pin',
    'nathni': 'nose pin',
    'laung': 'nose pin',
    'kada': 'bracelet',
    'kangan': 'bracelet',
    'bangdi': 'bracelet',
    'chudi': 'bracelet',
    'haar': 'necklace',
    'mala': 'necklace',
    'thushi': 'necklace',
    'galsari': 'necklace',
    'vati': 'pendant',
    'tanmaniya': 'pendant',
    'locket': 'pendant',
    'latkan': 'pendant',
    'anguthi': 'ring',
    'challa': 'ring',
    'chhalla': 'ring',
    'bichhiya': 'toe ring',
    'bichiya': 'toe ring',
    'jodvi': 'toe ring',
    'metti': 'toe ring',
    'jhumka': 'jhumki',
    'jhumke': 'jhumki',
    'nazariya': 'evil eye'
  };

  // Hindi filler dropped ONLY when a vernacular alias also fired — an
  // English query like "gifts for her" must never be rewritten.
  var STOPWORDS = ['ki', 'ka', 'ke', 'liye', 'wali', 'wala', 'mein', 'for'];

  var STOP = {};
  for (var i = 0; i < STOPWORDS.length; i++) STOP[STOPWORDS[i]] = true;

  // Returns the normalised query, or null when nothing should change.
  function normalize(raw) {
    if (!raw) return null;
    var s = String(raw).trim().toLowerCase().replace(/\s+/g, ' ');
    if (!s) return null;
    if (ALIASES[s]) return ALIASES[s] === s ? null : ALIASES[s];
    var toks = s.split(' ');
    if (toks.length < 2) return null; // single native-hit tokens stay untouched
    var aliasFired = false;
    for (var t = 0; t < toks.length; t++) {
      if (TOKEN_ALIASES[toks[t]]) { aliasFired = true; break; }
    }
    if (!aliasFired) return null;
    var out = [];
    for (var u = 0; u < toks.length; u++) {
      var tok = toks[u];
      if (STOP[tok]) continue;
      out.push(TOKEN_ALIASES[tok] || tok);
    }
    var joined = out.join(' ').replace(/\s+/g, ' ').trim();
    return joined && joined !== s ? joined : null;
  }

  /* CATEGORY REDIRECT (2026-08-22) — an exact category-name query goes to the
   * collection page (chips, filters, infinite scroll) instead of the generic
   * paginated /search grid. Baymard: 46% of stores fail this, and category
   * words dominate jewellery search.
   *
   * Rules:
   *  - WHOLE-QUERY exact match only, checked after the same normalisation the
   *    search uses — so "laung" reaches Nose Pins via ALIASES ("nose pin") and
   *    "chandi payal" reaches Anklets via TOKEN_ALIASES ("silver anklet")
   *    without either being listed here twice.
   *  - Handles below are the 11 category-strip collections, VERIFIED against
   *    templates/index.json category_strip on 2026-08-22 — do not add a route
   *    whose handle you have not verified the same way.
   *  - "necklace" routes to Pendants deliberately (the twin-consolidation made
   *    Pendants canonical; footer group is "Pendants & Necklaces").
   *  - Applied on form submit AND on landing at a pristine /search?q= URL
   *    (old links, bookmarks); any extra param (filters, page, sort) means the
   *    shopper is deep in search usage — never redirect those.
   */
  var COLLECTION_ROUTES = {};
  (function () {
    var routes = {
      '/collections/earrings': ['earrings', 'earring', 'ear rings', 'silver earrings', 'silver earring'],
      '/collections/pendants': ['pendants', 'pendant', 'necklace', 'necklaces', 'silver pendants', 'silver pendant'],
      '/collections/silver-rings': ['rings', 'ring', 'silver rings', 'silver ring', 'finger ring', 'finger rings'],
      '/collections/silver-nose-pins': ['nose pins', 'nose pin', 'nosepin', 'nosepins', 'nose ring', 'nose rings', 'nose stud', 'nose studs'],
      '/collections/silver-bracelets': ['bracelets', 'bracelet', 'silver bracelets', 'silver bracelet'],
      '/collections/silver-anklets': ['anklets', 'anklet', 'payal', 'payals', 'silver anklets', 'silver anklet'],
      '/collections/silver-jewellery-sets': ['sets', 'set', 'jewellery sets', 'jewellery set', 'jewelry sets', 'jewelry set', 'necklace set', 'necklace sets'],
      '/collections/mens': ['mens', 'men', "men's", 'mens jewellery', "men's jewellery", 'for him'],
      '/collections/silver-toe-rings': ['toe rings', 'toe ring', 'toerings', 'toering'],
      '/collections/enamel': ['kids', 'kids jewellery', 'enamel'],
      '/collections/stud-collection': ['studs', 'stud', 'stud earrings', 'ear studs', 'tops', 'ear tops']
    };
    for (var path in routes) {
      for (var r = 0; r < routes[path].length; r++) COLLECTION_ROUTES[routes[path][r]] = path;
    }
  })();

  function collectionFor(raw) {
    if (!raw) return null;
    var s = String(raw).trim().toLowerCase().replace(/\s+/g, ' ');
    if (!s) return null;
    if (COLLECTION_ROUTES[s]) return COLLECTION_ROUTES[s];
    var mapped = normalize(s);
    if (mapped && COLLECTION_ROUTES[mapped]) return COLLECTION_ROUTES[mapped];
    return null;
  }

  // Landing on a pristine /search?q=<category> (bookmark, old link, non-JS
  // submit) — replace() so the results flash never enters history.
  (function () {
    if (window.location.pathname !== '/search') return;
    var sp;
    try { sp = new URLSearchParams(window.location.search); } catch (e) { return; }
    var deep = false;
    sp.forEach(function (v, k) {
      if (k !== 'q' && k.indexOf('options[') !== 0 && k !== 'type') deep = true;
    });
    if (deep) return;
    var dest = collectionFor(sp.get('q'));
    if (dest) window.location.replace(dest);
  })();

  // ---- PredictiveSearch integration (only when Dawn's component is loaded) --
  // Top-level class declarations in classic scripts are visible to later
  // classic scripts; both files load deferred with this one after
  // predictive-search.js in layout/theme.liquid.
  if (typeof PredictiveSearch !== 'undefined' && PredictiveSearch.prototype) {
    var origGetSearchResults = PredictiveSearch.prototype.getSearchResults;
    PredictiveSearch.prototype.getSearchResults = function (searchTerm) {
      var mapped = normalize(searchTerm);
      return origGetSearchResults.call(this, mapped || searchTerm);
    };

    PredictiveSearch.prototype.updateSearchForTerm = function (previousTerm, newTerm) {
      var el = this.querySelector('[data-predictive-search-search-for-text]');
      if (!el) return;
      var text = el.innerText;
      if (!text || !previousTerm) return;
      var matches;
      try {
        var escaped = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        matches = text.match(new RegExp(escaped, 'g'));
      } catch (e) { return; }
      if (!matches || matches.length > 1) return;
      el.innerText = text.replace(previousTerm, newTerm);
    };
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    var forms = document.querySelectorAll('.hs-searchbar__form');

    forms.forEach(function (form) {
      var input = form.querySelector('input[type="search"]');
      if (!input) return;

      // -- submit: category queries go to the collection page; everything ----
      // -- else gets the canonical term rewritten into /search?q= ------------
      form.addEventListener('submit', function (e) {
        var root = form.closest('predictive-search');
        // If a dropdown option is selected, Dawn preventDefaults and clicks it
        // instead — leave the input alone in that case.
        if (root && root.querySelector('[aria-selected="true"] a')) return;
        var dest = collectionFor(input.value);
        if (dest) {
          e.preventDefault();
          window.location.assign(dest);
          return;
        }
        var mapped = normalize(input.value);
        if (mapped) input.value = mapped;
      });

      // -- trending panel: visible while focused and empty -------------------
      var trending = form.querySelector('[data-hs-trending]');
      if (trending) {
        var syncTrending = function () {
          trending.hidden = !!input.value.trim();
        };
        input.addEventListener('focus', syncTrending);
        input.addEventListener('input', syncTrending);
        // Same deferred-check pattern Dawn uses for its own dropdown, so a tap
        // on a chip lands before the panel hides.
        form.addEventListener('focusout', function () {
          setTimeout(function () {
            if (!form.contains(document.activeElement)) trending.hidden = true;
          });
        });
      }

      // -- rotating placeholder ---------------------------------------------
      var phAttr = input.getAttribute('data-hs-placeholders');
      if (phAttr && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        var phrases = phAttr.split(/\r?\n/).map(function (s) { return s.trim(); }).filter(Boolean);
        if (phrases.length > 1) {
          var idx = 0;
          var stopped = false;
          var timer = setInterval(function () {
            if (stopped) return;
            if (input.value) { stopped = true; clearInterval(timer); return; }
            if (document.activeElement === input) return; // pause while focused
            idx = (idx + 1) % phrases.length;
            input.setAttribute('placeholder', phrases[idx]);
          }, 3500);
        }
      }
    });
  });
})();
